import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CalendarClock, CheckCircle2, Loader2, Send } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { useCampaignAttribution } from '@/hooks/useCampaignAttribution';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * CourseRegisterForm
 *
 * One reusable inline form serving both funnel entry points for the September
 * 2026 AI Quick Start cohort (issue #324). Posts to POST /course-register
 * (functions/course-register.js) with the campaign attribution payload from
 * useCampaignAttribution, so every registration is joinable back to the ad
 * creative that produced it.
 *
 * Props:
 *  - courseSlug  {string}  required — e.g. 'ai-quick-start-hr'
 *  - courseTitle {string}  required — display title, also stored on the lead row
 *  - intent      {string}  'waitlist' | 'free-enrolment' (default 'waitlist')
 *  - cohort      {string}  e.g. '2026-09'
 *
 * NOTE — no policy links in this component, deliberately.
 * CoursePage.jsx (lines 71–90) runs a DOM sweep on mount that removes EVERY
 * anchor whose href matches /terms|privacy|refund/i, including ones rendered
 * by children. A privacy-policy link in the consent copy below would be
 * silently stripped from the live page, leaving consent text that references a
 * link the visitor cannot see. The consent wording is therefore self-contained
 * and points at the footer instead.
 */
export default function CourseRegisterForm({
  courseSlug,
  courseTitle,
  intent = 'waitlist',
  cohort,
}) {
  const { toast } = useToast();
  const attribution = useCampaignAttribution();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [role, setRole] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const isWaitlist = intent !== 'free-enrolment';
  const fieldId = (suffix) => `course-register-${courseSlug}-${suffix}`;

  const validate = () => {
    const next = {};
    if (!name || name.trim().length < 2) {
      next.name = 'Please enter your name.';
    }
    if (!email || !EMAIL_PATTERN.test(email.trim())) {
      next.email = 'Please enter a valid email address.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;

    setSubmitting(true);
    try {
      const response = await fetch('/course-register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          organisation: organisation.trim() || undefined,
          role: role.trim() || undefined,
          courseSlug,
          courseTitle,
          intent,
          cohort: cohort || undefined,
          // Campaign attribution — captured on first landing, carried through
          // the session by useCampaignAttribution.
          utm_source: attribution.utm_source || undefined,
          utm_medium: attribution.utm_medium || undefined,
          utm_campaign: attribution.utm_campaign || undefined,
          utm_content: attribution.utm_content || undefined,
          utm_term: attribution.utm_term || undefined,
          referrer: attribution.referrer || undefined,
          landingPath: attribution.landingPath || undefined,
        }),
      });

      if (response.ok) {
        let message = isWaitlist
          ? "You're on the list — check your inbox for confirmation."
          : "You're registered — check your inbox for confirmation.";
        try {
          const data = await response.json();
          if (data?.message && typeof data.message === 'string') message = data.message;
        } catch {
          // ignore body parse errors — a 200 means the lead was saved
        }
        toast({ title: isWaitlist ? "You're on the list" : "You're registered", description: message });
        setSubmitted(true);
        return;
      }

      let message = 'Something went wrong saving your registration. Please try again or email info@educ4te.com.';
      try {
        const data = await response.json();
        if (data?.error && typeof data.error === 'string') message = data.error;
        else if (data?.message && typeof data.message === 'string') message = data.message;
      } catch {
        // ignore body parse errors
      }

      if (response.status === 400 || response.status === 429) {
        toast({ title: "We couldn't register you", description: message, variant: 'destructive' });
      } else {
        toast({
          title: 'Something went wrong',
          description: 'Something went wrong saving your registration. Please try again or email info@educ4te.com.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      console.error('[CourseRegisterForm] course-register error:', err);
      toast({
        title: 'Something went wrong',
        description: 'Something went wrong saving your registration. Please try again or email info@educ4te.com.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm"
        role="status"
        aria-live="polite"
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 shrink-0 text-[#422680]" size={22} />
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              {isWaitlist ? "You're on the list" : "Your seat is reserved"}
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              We've emailed a confirmation to <span className="font-medium text-gray-800">{email.trim()}</span>.
              {isWaitlist
                ? ' You will hear from us before seats open publicly.'
                : ' We will send your joining details closer to the start date.'}
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  const inputClass = (hasError) =>
    `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#422680] focus:border-transparent disabled:bg-gray-50 ${
      hasError ? 'border-[#F54952]' : 'border-gray-200'
    }`;

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="bg-gradient-to-r from-[#422680] via-[#660F56] to-[#F54952] px-6 py-5 text-white">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-white/15 p-2">
            <CalendarClock size={20} />
          </div>
          <div>
            <h3 className="text-lg font-bold leading-tight md:text-xl">
              {isWaitlist ? 'Join the waitlist' : 'Register your place'}
            </h3>
            <p className="mt-0.5 text-sm text-white/80">
              {courseTitle}
              {cohort ? ` — ${formatCohort(cohort)} cohort` : ''}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 p-6" noValidate>
        <p className="text-sm text-gray-600">
          {isWaitlist
            ? 'Seats are limited and released to the waitlist first. Tell us where to reach you and we will let you know as soon as dates are confirmed.'
            : 'Tell us where to reach you and we will send your enrolment link and joining details.'}
        </p>

        <div>
          <label htmlFor={fieldId('name')} className="mb-1 block text-sm font-medium text-gray-700">
            Name <span className="text-[#F54952]">*</span>
          </label>
          <input
            id={fieldId('name')}
            type="text"
            required
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={submitting}
            placeholder="Full name"
            className={inputClass(errors.name)}
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? fieldId('name-error') : undefined}
          />
          {errors.name && (
            <p id={fieldId('name-error')} className="mt-1 text-xs text-[#F54952]">{errors.name}</p>
          )}
        </div>

        <div>
          <label htmlFor={fieldId('email')} className="mb-1 block text-sm font-medium text-gray-700">
            Email <span className="text-[#F54952]">*</span>
          </label>
          <input
            id={fieldId('email')}
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            placeholder="you@organisation.com.au"
            className={inputClass(errors.email)}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? fieldId('email-error') : undefined}
          />
          {errors.email && (
            <p id={fieldId('email-error')} className="mt-1 text-xs text-[#F54952]">{errors.email}</p>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor={fieldId('organisation')} className="mb-1 block text-sm font-medium text-gray-700">
              Organisation
            </label>
            <input
              id={fieldId('organisation')}
              type="text"
              autoComplete="organization"
              value={organisation}
              onChange={(e) => setOrganisation(e.target.value)}
              disabled={submitting}
              placeholder="Organisation (optional)"
              className={inputClass(false)}
            />
          </div>

          <div>
            <label htmlFor={fieldId('role')} className="mb-1 block text-sm font-medium text-gray-700">
              Role
            </label>
            <input
              id={fieldId('role')}
              type="text"
              autoComplete="organization-title"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              disabled={submitting}
              placeholder="Your role (optional)"
              className={inputClass(false)}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#422680] via-[#660F56] to-[#F54952] py-3 font-semibold text-white transition-all hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Send size={18} />
              {isWaitlist ? 'Join the waitlist' : 'Register my place'}
            </>
          )}
        </button>

        {/*
          Consent copy — intentionally contains no anchor. CoursePage.jsx removes
          every link matching terms/privacy/refund from the page on mount, so any
          policy link placed here would vanish. Our handling of personal
          information is set out in the site footer.
        */}
        <p className="text-xs leading-relaxed text-gray-500">
          We will use your details to contact you about this course and related EDUC4TE training. You can
          unsubscribe from every email we send. We never sell or share your information.
        </p>
      </form>
    </div>
  );
}

function formatCohort(cohort) {
  const match = /^(\d{4})-(\d{2})$/.exec(cohort);
  if (!match) return cohort;
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const name = months[parseInt(match[2], 10) - 1];
  return name ? `${name} ${match[1]}` : cohort;
}
